import { bigIntReplacer } from "libs/bigint_helpers";
import ErrorHandling from "libs/error_handling";
import { V3 } from "libs/mjs";
import Toast from "libs/toast";
import { coalesce, map3, numberArrayToVector3, roundTo } from "libs/utils";
import window, { location } from "libs/window";
import isEqual from "lodash-es/isEqual";
import partition from "lodash-es/partition";
import size from "lodash-es/size";
import throttle from "lodash-es/throttle";
import messages from "messages";
import type { AdditionalCoordinate, APIDataset } from "types/api_types";
import { type APIAnnotationType, APICompoundTypeEnum } from "types/api_types";
import type { Mutable } from "types/type_utils";
import { validateUrlStateJSON } from "types/validation";
import type { MappingType, Vector3, ViewMode } from "viewer/constants";
import constants, { MappingStatusEnum, ViewModeValues } from "viewer/constants";
import { applyState } from "viewer/controller/apply_url_state";
import { getPosition } from "viewer/model/accessors/flycam_accessor";
import { getMeshesForCurrentAdditionalCoordinates } from "viewer/model/accessors/volumetracing_accessor";
import {
  additionalCoordinateToKeyValue,
  parseAdditionalCoordinateKey,
} from "viewer/model/helpers/nml_helpers";
import type {
  CameraData,
  DatasetLayerConfiguration,
  MeshInformation,
  WebknossosState,
} from "viewer/store";
import Store from "viewer/store";

const MAX_UPDATE_INTERVAL = 1000;
const MINIMUM_VALID_CSV_LENGTH = 5;

type BaseMeshUrlDescriptor = {
  readonly segmentId: bigint;
  readonly seedPosition: Vector3;
  readonly seedAdditionalCoordinates?: AdditionalCoordinate[];
};
type AdHocMeshUrlDescriptor = BaseMeshUrlDescriptor & {
  readonly isPrecomputed: false;
  mappingName: string | null | undefined;
  mappingType: MappingType | null | undefined;
};
type PrecomputedMeshUrlDescriptor = BaseMeshUrlDescriptor & {
  readonly isPrecomputed: true;
  meshFileName: string;
};
type MeshUrlDescriptor = AdHocMeshUrlDescriptor | PrecomputedMeshUrlDescriptor;
export type DirectLayerSpecificProps = Mutable<
  Partial<
    Pick<
      DatasetLayerConfiguration,
      "isDisabled" | "intensityRange" | "color" | "isInverted" | "gammaCorrectionValue"
    >
  >
>;
export type TdCameraUrlState = Pick<
  CameraData,
  "position" | "up" | "left" | "right" | "top" | "bottom"
>;
export type UrlStateByLayer = Record<
  string,
  {
    meshInfo?: {
      meshFileName: string | null | undefined;
      meshes: Array<MeshUrlDescriptor>;
    };
    mappingInfo?: {
      mappingName: string;
      mappingType: MappingType;
      agglomerateIdsToImport?: Array<bigint>;
    };
    connectomeInfo?: {
      connectomeName: string;
      agglomerateIdsToImport?: Array<bigint>;
    };
  } & DirectLayerSpecificProps
>;

function mapMeshInfoToUrlMeshDescriptor(meshInfo: MeshInformation): MeshUrlDescriptor {
  const { segmentId, seedPosition } = meshInfo;
  const baseUrlDescriptor: BaseMeshUrlDescriptor = {
    segmentId,
    seedPosition: V3.floor(seedPosition),
  };

  if (meshInfo.isPrecomputed) {
    const { meshFileName } = meshInfo;
    return { ...baseUrlDescriptor, isPrecomputed: true, meshFileName };
  } else {
    const { mappingName, mappingType } = meshInfo;
    return { ...baseUrlDescriptor, isPrecomputed: false, mappingName, mappingType };
  }
}

// Extracts the dataset name from view or sandbox URLs.
export function getDatasetNameFromLocation(location: Location): string | undefined {
  // URL format: /datasets/<dataset-name>-<dataset-id>/<view|sandbox/type>...
  const pathnameParts = location.pathname.split("/").slice(1); // First string is empty as pathname start with a /.
  const endOfDatasetName = pathnameParts[1].lastIndexOf("-");
  if (endOfDatasetName <= 0) {
    return undefined;
  }
  const datasetNameInURL = pathnameParts[1].substring(0, endOfDatasetName);
  return datasetNameInURL;
}

export function getUpdatedPathnameWithNewDatasetName(
  location: (typeof window)["location"],
  dataset: APIDataset,
): string {
  const pathnameParts = location.pathname.split("/").slice(1); // First string is empty as pathname start with a /.
  const newNameAndIdPart = `${dataset.name}-${dataset.id}`;
  const newPathname = `/${pathnameParts[0]}/${newNameAndIdPart}/${pathnameParts.slice(2).join("/")}`;
  return newPathname;
}

// If the type of UrlManagerState changes, the following files need to be updated:
// docs/sharing/annotation_sharing.md#sharing-link-format
// frontend/javascripts/types/schemas/url_state.schema.ts
export type UrlManagerState = {
  position?: Vector3;
  mode?: ViewMode;
  zoomStep?: number;
  activeNode?: number;
  rotation?: Vector3;
  stateByLayer?: UrlStateByLayer;
  additionalCoordinates?: AdditionalCoordinate[] | null;
  nativelyRenderedLayerName?: string | null;
  clippingDistance?: number;
  clipSkeletonToCurrentSection?: boolean;
  tdCamera?: TdCameraUrlState;
};
export type PartialUrlManagerState = Partial<UrlManagerState>;

class UrlManager {
  baseUrl: string = "";
  initialState: PartialUrlManagerState = {};
  stopStoreListening?: () => void;

  initialize() {
    this.baseUrl = location.pathname + location.search;
    this.initialState = this.parseUrlHash();
  }

  reset(keepUrlState: boolean = false, keepUrlSearch: boolean = false): void {
    // don't use location.hash = ""; since it refreshes the page
    if (!keepUrlState) {
      window.history.replaceState(
        window.history.state,
        "",
        location.pathname + (keepUrlSearch ? location.search : ""),
      );
    }

    this.initialize();
  }

  changeBaseUrl(newBaseUrl: string) {
    this.baseUrl = newBaseUrl;
    this.updateUnthrottled();
  }

  update = throttle(() => this.updateUnthrottled(), MAX_UPDATE_INTERVAL);

  updateUnthrottled() {
    const url = this.buildUrl();
    // Keep the existing history state, since react-router stores its own data there.
    window.history.replaceState(window.history.state, "", url);
  }

  onHashChange = () => {
    this.updateToHash();
  };

  updateToHash(optionalHash?: string) {
    const urlState = this.parseUrlHash(optionalHash);
    applyState(urlState);
  }

  parseUrlHash(optionalHash?: string): PartialUrlManagerState {
    // Cut off the #
    const urlHash = decodeURIComponent(optionalHash ?? location.hash.slice(1));

    if (urlHash.includes("{")) {
      // The hash is in json format
      return this.parseUrlHashJson(urlHash);
    } else if (urlHash.split(",")[0].includes("=")) {
      // The hash was changed by a comment link
      return this.parseUrlHashCommentLink(urlHash);
    } else {
      // The hash is in csv format (it can also contain
      // key=value pairs, but only after the first mandatory
      // CSV values).
      return this.parseUrlHashCsv(urlHash);
    }
  }

  parseUrlHashCommentLink(urlHash: string): PartialUrlManagerState {
    // Comment link format:
    // activeNode=12 or position=1,2,3
    const [key, value] = urlHash.split("=");
    // The value can either be a single number or multiple numbers delimited by a ,
    return {
      [key]: value.includes(",") ? value.split(",").map(Number) : Number(value),
    };
  }

  parseUrlHashJson(urlHash: string): PartialUrlManagerState {
    // State json format:
    // { "position": Vector3, "mode": number, "zoomStep": number, ...}
    try {
      // Oblique mode was removed; migrate old URLs that reference it to orthogonal.
      const migratedHash = urlHash.replace(/"mode"\s*:\s*"oblique"/, '"mode":"orthogonal"');
      return validateUrlStateJSON(migratedHash);
    } catch (e) {
      Toast.error(messages["tracing.invalid_json_url_hash"]);
      console.error(e);
      ErrorHandling.notify(e as Error);
      return {};
    }
  }

  parseUrlHashCsv(urlHash: string): PartialUrlManagerState {
    // State string format:
    // x,y,z,mode,zoomStep[,rotX,rotY,rotZ][,activeNode][,key=value]*
    const state: PartialUrlManagerState = {};

    if (!urlHash) {
      return state;
    }

    const commaSeparatedValues = urlHash.split(",");
    const [baseValues, keyValuePairStrings] = partition(
      commaSeparatedValues,
      (value) => !value.includes("="),
    );
    const stateArray = baseValues.map(Number);
    const validStateArray = stateArray.map((value) => (!Number.isNaN(value) ? value : 0));

    if (validStateArray.length >= MINIMUM_VALID_CSV_LENGTH) {
      const positionValues = validStateArray.slice(0, 3);
      state.position = numberArrayToVector3(positionValues);
      const modeString = ViewModeValues[validStateArray[3]];

      if (modeString) {
        state.mode = modeString;
      } else {
        // Let's default to MODE_PLANE_TRACING
        state.mode = constants.MODE_PLANE_TRACING;
      }

      // default to zoom step 1
      state.zoomStep = validStateArray[4] !== 0 ? validStateArray[4] : 1;

      if (validStateArray.length >= 8) {
        state.rotation = numberArrayToVector3(validStateArray.slice(5, 8));

        if (validStateArray[8] != null) {
          state.activeNode = validStateArray[8];
        }
      } else if (validStateArray[5] != null) {
        state.activeNode = validStateArray[5];
      }
    }

    const additionalCoordinates = [];
    const keyValuePairs = keyValuePairStrings.map((keyValueStr) => keyValueStr.split("=", 2));
    for (const [key, value] of keyValuePairs) {
      const coordinateName = parseAdditionalCoordinateKey(key, true);
      if (coordinateName != null) {
        additionalCoordinates.push({
          name: coordinateName,
          value: Number.parseFloat(value),
        });
      }
    }

    if (additionalCoordinates.length > 0) {
      state.additionalCoordinates = additionalCoordinates;
    }

    return state;
  }

  startUrlUpdater(): void {
    this.stopStoreListening = Store.subscribe(() => this.update());

    window.onhashchange = () => this.onHashChange();
  }

  stopUrlUpdater(): void {
    if (this.stopStoreListening != null) {
      this.stopStoreListening();
    }
    this.update.cancel();
    window.onhashchange = null;
  }

  getUrlState(state: WebknossosState): UrlManagerState & { mode: ViewMode } {
    const position: Vector3 = V3.floor(getPosition(state.flycam));
    const { viewMode: mode } = state.temporaryConfiguration;
    const zoomStep = roundTo(state.flycam.zoomStep, 3);
    const flycamRotation = map3((e) => roundTo(e, 2), state.flycam.rotation);
    const rotation = {
      // Keep rotation state empty if no rotation is active to have shorter url hashes.
      rotation: isEqual(flycamRotation, [0, 0, 0]) ? undefined : flycamRotation,
    };
    const activeNode = state.annotation.skeleton?.activeNodeId;
    const activeNodeOptional = activeNode != null ? { activeNode } : {};
    const stateByLayer: UrlStateByLayer = {};

    for (const layerName of Object.keys(state.temporaryConfiguration.activeMappingByLayer)) {
      const mappingInfo = state.temporaryConfiguration.activeMappingByLayer[layerName];

      if (
        mappingInfo.mappingStatus === MappingStatusEnum.ENABLED &&
        mappingInfo.mappingName != null
      ) {
        const { mappingName, mappingType } = mappingInfo;
        stateByLayer[layerName] = {
          ...stateByLayer[layerName],
          mappingInfo: {
            mappingName,
            mappingType,
          },
        };
      }
    }

    for (const layerName of Object.keys(state.localSegmentationStateByLayer)) {
      const { currentMeshFile } = state.localSegmentationStateByLayer[layerName];
      const currentMeshFileName = currentMeshFile?.name;
      const localMeshes = getMeshesForCurrentAdditionalCoordinates(state, layerName);
      const meshes =
        localMeshes != null
          ? Object.values(localMeshes)
              .filter(({ isVisible }) => isVisible)
              .map(mapMeshInfoToUrlMeshDescriptor)
          : [];

      if (currentMeshFileName != null || meshes.length > 0) {
        stateByLayer[layerName] = {
          ...stateByLayer[layerName],
          meshInfo: {
            meshFileName: currentMeshFileName,
            meshes,
          },
        };
      }
    }

    for (const layerName of Object.keys(state.datasetConfiguration.layers)) {
      const layerConfiguration = state.datasetConfiguration.layers[layerName];

      if (layerConfiguration != null) {
        stateByLayer[layerName] = {
          ...stateByLayer[layerName],
          isDisabled: layerConfiguration.isDisabled,
          // manually setting the tuple is needed due to layerConfiguration.intensityRange being readonly.
          intensityRange: layerConfiguration.intensityRange && [
            layerConfiguration.intensityRange[0],
            layerConfiguration.intensityRange[1],
          ],
          color: layerConfiguration.color,
          isInverted: layerConfiguration.isInverted,
          gammaCorrectionValue: layerConfiguration.gammaCorrectionValue,
        };
      }
    }

    const annotation = state.annotation;
    if (annotation.skeleton != null) {
      const { showSkeletons } = state.localSkeletonState;
      const layerName = "Skeleton";

      stateByLayer[layerName] = {
        ...stateByLayer[layerName],
        isDisabled: !showSkeletons,
      };
    }

    const stateByLayerOptional =
      size(stateByLayer) > 0
        ? {
            stateByLayer,
          }
        : {};
    const { clippingDistance, clipSkeletonToCurrentSection } = state.userConfiguration;

    const tdCamera = state.viewModeData.plane.tdCamera;
    // The td camera defaults to a degenerate, zero-sized frustum until it is
    // initialized by the CameraController. Omit it in that case to avoid
    // encoding meaningless zeros.
    const tdCameraOptional =
      tdCamera.left !== tdCamera.right && tdCamera.top !== tdCamera.bottom
        ? {
            tdCamera: {
              position: map3((e) => roundTo(e, 2), tdCamera.position),
              up: map3((e) => roundTo(e, 2), tdCamera.up),
              left: roundTo(tdCamera.left, 2),
              right: roundTo(tdCamera.right, 2),
              top: roundTo(tdCamera.top, 2),
              bottom: roundTo(tdCamera.bottom, 2),
            },
          }
        : {};

    return {
      position,
      mode,
      zoomStep,
      additionalCoordinates: state.flycam.additionalCoordinates,
      nativelyRenderedLayerName: state.datasetConfiguration.nativelyRenderedLayerName,
      clippingDistance,
      clipSkeletonToCurrentSection,
      ...rotation,
      ...activeNodeOptional,
      ...stateByLayerOptional,
      ...tdCameraOptional,
    };
  }

  buildUrlHashCsv(state: WebknossosState): string {
    const { position = [], mode, zoomStep, rotation = [], activeNode } = this.getUrlState(state);
    const viewModeIndex = ViewModeValues.indexOf(mode);
    const activeNodeArray = activeNode != null ? [activeNode] : [];
    const keyValuePairs = (state.flycam.additionalCoordinates || []).map((coord) =>
      additionalCoordinateToKeyValue(coord, true),
    );

    return [
      ...position,
      viewModeIndex,
      zoomStep,
      ...rotation,
      ...activeNodeArray,
      ...keyValuePairs.map(([key, value]) => `${key}=${value}`),
    ].join(",");
  }

  buildUrlHashJson(state: WebknossosState): string {
    const urlState = this.getUrlState(state);
    return encodeUrlHash(JSON.stringify(urlState, bigIntReplacer));
  }

  buildUrl(): string {
    const state = Store.getState();
    const hash = this.buildUrlHashCsv(state);
    const newBaseUrl = updateTypeAndId(
      this.baseUrl,
      state.annotation.annotationType,
      state.annotation.annotationId,
    );
    return `${newBaseUrl}#${hash}`;
  }
}

export function updateTypeAndId(
  baseUrl: string,
  annotationType: APIAnnotationType,
  annotationId: string,
): string {
  // Update the baseUrl with a potentially new annotation id and or tracing type.
  // There are two possible routes (/annotations or /datasets), but the annotation id
  // will only ever be updated for the annotations route as the other route is for
  // dataset viewing only.
  // Note that for non-compounds (i.e., Explorationals and Tasks), these types won't
  // appear in the URL. For backwards compatibility old URLs containing Explorational/Task
  // still work, but they are immediately redirected to the URL version without that part.

  const maybeCompoundType = coalesce(
    APICompoundTypeEnum,
    annotationType as unknown as APICompoundTypeEnum,
  );
  if (maybeCompoundType == null) {
    return baseUrl.replace(
      /^(.*\/annotations)\/([^/?]*)(\/?.*)$/,
      (_all, base, _id, rest) => `${base}/${annotationId}${rest}`,
    );
  } else {
    return baseUrl.replace(
      /^(.*\/annotations)\/(.*?)\/([^/?]*)(\/?.*)$/,
      (_all, base, _type, _id, rest) => `${base}/${maybeCompoundType}/${annotationId}${rest}`,
    );
  }
}
// encodeURIComponent encodes all characters except [A-Za-z0-9] - _ . ! ~ * ' ( )
// see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
// The url hash can contain ! $ & ' ( ) * + , ; =  - . _ ~ : @ / ? or [a-zA-Z0-9] or %[0-9a-fA-F]{2}
// see https://stackoverflow.com/a/2849800
// Whitelist the characters that are part of the second list, but not of the first as they don't need to be encoded
// for better url readability
const urlHashCharacterWhiteList = ["$", "&", "+", ",", ";", "=", ":", "@", "/", "?"];
// Build lookup table from encoded to decoded value
const encodedCharacterToDecodedCharacter = urlHashCharacterWhiteList.reduce(
  (obj, decodedValue) => {
    obj[encodeURIComponent(decodedValue)] = decodedValue;
    return obj;
  },
  {} as Record<string, string>,
);
// Build RegExp that matches each of the encoded characters (%xy) and a function to decode it
const re = new RegExp(Object.keys(encodedCharacterToDecodedCharacter).join("|"), "gi");

const decodeWhitelistedCharacters = (matched: string) =>
  encodedCharacterToDecodedCharacter[matched];

export function encodeUrlHash(unencodedHash: string): string {
  const urlEncodedHash = encodeURIComponent(unencodedHash);
  return urlEncodedHash.replace(re, decodeWhitelistedCharacters);
}
export default new UrlManager();
